import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MuiAutocomplete from '@mui/material/Autocomplete';
import { useFormControl } from '@mui/material/FormControl';
import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { SvgIconComponent } from '@mui/icons-material';

import Icon from 'shared/atoms/Icon';
import IconButton, { type IconButtonProps } from 'shared/atoms/IconButton';
import SearchField from 'shared/atoms/SearchField';

import type { BackgroundThemes } from 'shared/const';
import type { InputType } from 'shared/atoms/Input/const';
import type { SxProps, Theme } from '@mui/material/styles';
import theme from 'shared/themes/default';

interface AutocompleteOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  icon?: SvgIconComponent;
  [key: string]: unknown;
}

interface AutocompleteProps<T = AutocompleteOption> {
  autoFocus?: boolean;
  blurOnSelect?: boolean;
  clearOnSelect?: boolean;
  disabled?: boolean;
  filterOptions?: boolean;
  freeSolo?: boolean;
  getAllOptions?: () => Promise<T[]>;
  getOptionKey?: (option: T) => string;
  getOptionLabel?: (option: T) => string;
  iconActions?: Omit<
    IconButtonProps,
    'ariaLabel' | 'icon' | 'small' | 'sx' | 'sxIcon' | 'theme' | 'type' | 'user'
  >;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  onClear?: () => void;
  onChange?: (value: T | null) => void;
  onCreateOption?: (value: string) => void;
  onExpand?: () => void;
  onInputChange?: (value: string) => void;
  optionProps?: (
    option: T
  ) => Omit<MenuItemProps, 'children' | 'key' | 'value'>;
  options?: T[];
  placeholder?: string;
  preInputValue?: string;
  preSelectedValue?: T | null;
  shrinked?: boolean;
  theme?: BackgroundThemes;
  type?: InputType;
}

const Autocomplete = <T extends AutocompleteOption = AutocompleteOption>({
  autoFocus,
  blurOnSelect = false,
  clearOnSelect,
  disabled: disabledProp,
  filterOptions,
  freeSolo = false,
  getAllOptions,
  getOptionKey,
  getOptionLabel = (option: T) => option.label,
  iconActions,
  isOptionEqualToValue = (option: T, value: T) => option.value === value.value,
  loading,
  loadingText,
  noOptionsText,
  onClear,
  onChange,
  onCreateOption,
  onExpand,
  onInputChange,
  optionProps = () => ({}),
  options,
  placeholder,
  preInputValue,
  preSelectedValue = null,
  shrinked,
  theme: themeLayout = 'dark',
  type = 'primary',
}: AutocompleteProps<T>) => {
  const [inputValue, setInputValue] = useState(preInputValue || '');
  const [value, setValue] = useState<T | null>(preSelectedValue || null);
  const [open, setOpen] = useState(false);
  const [optionsToSelect, setOptionsToSelect] = useState<T[] | undefined>(
    options
  );
  const [isShrinked, setIsShrinked] = useState(shrinked);

  const { t } = useTranslation();

  // Input component uses FormControl internally, but autocomplete does not, so this is needed for autocomplete in forms
  // The prop gets priority
  const { disabled: disabledFormControl } = useFormControl() || {};

  const disabled =
    disabledProp === undefined ? disabledFormControl : disabledProp;

  const sx = {
    '.MuiAutocomplete-list': {
      borderRadius: '6px',
    },
    '.MuiOutlinedInput-root': {
      paddingBottom: 0,
      paddingTop: 0,
    },
    '.MuiAutocomplete-clearIndicator': {
      color: 'inherit',
    },
    '.MuiAutocomplete-popupIndicator': {
      color: 'inherit',
    },
  };

  const sxPaperBase: Record<string, unknown> = {
    mt: '2px',
    boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.15)',
  };
  const sxListBoxBase: Record<string, unknown> = {
    p: '0.5rem 0',
    fontWeight: 400,
  };

  let sxPaper: Record<string, unknown>;
  let sxListBox: Record<string, unknown>;

  if (type === 'primary' || (type === 'secondary' && themeLayout === 'light')) {
    sxPaper = {
      ...sxPaperBase,
      '.MuiAutocomplete-loading, .MuiAutocomplete-noOptions': {
        color: theme.palette.disabledDark,
      },
    };

    sxListBox = {
      ...sxListBoxBase,
      color: `${theme.palette.mainDark} !important`,
      font: `1rem ${theme.fonts.primary}`,
      '.MuiAutocomplete-option': {
        gap: '0.5rem',
        p: '0.25rem 0.75rem',
        minHeight: 0,
        '&.Mui-focused': {
          backgroundColor: `${theme.palette.darkDistinct}`,
          color: theme.palette.lightPure,
          fontWeight: 600,
        },
      },
      '.MuiAutocomplete-option[aria-disabled="true"]': {
        color: theme.palette.disabledDark,
        opacity: 1,
      },
      '.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: `${theme.palette.lightInteracting} !important`,
        color: theme.palette.darkDistinct,
        '&.Mui-focused': {
          backgroundColor: `${theme.palette.disabledLight} !important`,
          color: theme.palette.darkDistinct,
          fontWeight: 600,
        },
      },
    };

    if (freeSolo) {
      sxListBox = {
        ...sxListBox,
        border: `1px solid ${theme.palette.disabledLight}`,
        borderRadius: '4px',
      };
    } else {
      sxPaper = {
        ...sxPaper,
        border: `1px solid ${theme.palette.disabledLight}`,
      };
    }
  } else if (type === 'secondary') {
    sxPaper = {
      ...sxPaperBase,
      background: theme.palette.mainDark,
      '.MuiAutocomplete-loading, .MuiAutocomplete-noOptions': {
        color: theme.palette.disabledLight,
      },
    };

    sxListBox = {
      ...sxListBoxBase,
      font: `1rem ${theme.fonts.primary}`,
      color: theme.palette.lightPure,
      '.MuiAutocomplete-option': {
        p: '0.25rem 0.75rem',
        minHeight: 0,
        '&.Mui-focused': {
          backgroundColor: `${theme.palette.lightInteracting}`,
          color: theme.palette.mainDark,
          fontWeight: 600,
        },
      },
      '.MuiAutocomplete-option[aria-disabled="true"]': {
        color: theme.palette.disabledDark,
        opacity: 1,
      },
      '.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: `${theme.palette.darkDistinct} !important`,
        color: theme.palette.lightPure,
        '&.Mui-focused': {
          backgroundColor: `${theme.palette.disabledLight} !important`,
          color: theme.palette.darkDistinct,
          fontWeight: 600,
        },
      },
    };

    if (freeSolo) {
      sxListBox = {
        ...sxListBox,
        border: `1px solid ${theme.palette.lightInteracting}`,
        borderRadius: '4px',
      };
    } else {
      sxPaper = {
        ...sxPaper,
        border: `1px solid ${theme.palette.lightInteracting}`,
      };
    }
  } else {
    sxPaper = sxPaperBase;
    sxListBox = sxListBoxBase;
  }

  useEffect(() => {
    const loadOptions = async () => {
      if (getAllOptions) {
        const loadedOptions = await getAllOptions();
        setOptionsToSelect(loadedOptions || []);
      }
    };

    if (open && getAllOptions && !optionsToSelect) {
      loadOptions();
    }
  }, [open]);

  useEffect(() => {
    setOptionsToSelect(options);
  }, [options]);

  useEffect(() => {
    setValue(preSelectedValue);
  }, [preSelectedValue]);

  const triggerExpanding = () => {
    setIsShrinked(false);
    if (onExpand) {
      onExpand();
    }
  };

  const triggerShrinking = () => {
    setIsShrinked(true);
    if (onClear) {
      onClear();
    }
  };

  const isLoading = loading || (open && !optionsToSelect);

  if (shrinked && isShrinked) {
    return (
      <IconButton
        onClick={triggerExpanding}
        ariaLabel={t('generic.search')}
        disabled={disabled}
        icon={SearchIcon}
        sxIcon={{
          transform: 'rotate(90deg)',
        }}
        theme="light"
        type="secondary"
      />
    );
  }

  return (
    <MuiAutocomplete
      componentsProps={{ paper: { sx: sxPaper as SxProps<Theme> } }}
      blurOnSelect={blurOnSelect}
      disabled={disabled}
      filterOptions={
        getAllOptions || filterOptions ? undefined : (option) => option
      }
      freeSolo={freeSolo}
      fullWidth
      getOptionDisabled={(option) => (option as T).disabled || false}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return getOptionLabel(option as T);
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      inputValue={inputValue}
      ListboxProps={{ sx: sxListBox as SxProps<Theme> }}
      loading={isLoading}
      loadingText={loadingText || t('generic.loadingText')}
      noOptionsText={noOptionsText || t('generic.noSearchResults')}
      onChange={(event, value, reason) => {
        if (event) {
          event.preventDefault();
        }

        if (reason === 'createOption') {
          onCreateOption?.(value as string);
        } else {
          if (reason === 'clear') {
            triggerShrinking();
          }

          onChange?.(value as T | null);
          setValue(value as T | null);
          if (clearOnSelect) {
            setValue(null);
            setInputValue('');
          }
        }
      }}
      onClose={(_, reason) => {
        setOpen(false);
        if (shrinked && reason === 'blur') {
          triggerShrinking();
        }
      }}
      onInputChange={(event, value) => {
        if (event) {
          event.preventDefault();
        }
        onInputChange?.(value);
        setInputValue(value);
      }}
      onOpen={() => setOpen(true)}
      open={open}
      options={optionsToSelect || []}
      popupIcon={<ExpandMore />}
      renderInput={(params) => {
        const { inputProps, InputProps } = params;
        const { className, ...otherInputProps } = inputProps;
        const { endAdornment } = InputProps;
        return (
          <div ref={InputProps.ref}>
            <SearchField
              autoFocus={autoFocus}
              disabled={disabled}
              endAdornment={endAdornment as React.ReactNode}
              iconActions={iconActions}
              inputProps={
                otherInputProps as React.InputHTMLAttributes<HTMLInputElement>
              }
              loading={isLoading}
              placeholder={placeholder || t('generic.search')}
              theme={themeLayout}
              type={type}
            />
          </div>
        );
      }}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          {...optionProps(option as T)}
          key={
            getOptionKey
              ? getOptionKey(option as T)
              : getOptionLabel(option as T)
          }
        >
          {(option as T).icon && (
            <Icon icon={(option as T).icon as SvgIconComponent} />
          )}
          {getOptionLabel(option as T)}
        </MenuItem>
      )}
      value={value}
      sx={sx}
    />
  );
};

export default Autocomplete;
export type { AutocompleteProps, AutocompleteOption };
